import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, errMsg, MEMBERS } from "@/lib/api";

const OPTS = [...MEMBERS, "OT8"];
export const pcsOf = (sel) => sel.reduce((n, m) => n + (m === "OT8" ? 8 : 1), 0);
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const Chips = ({ value, onChange, prefix, opts = OPTS }) => (
  <div className="flex flex-wrap gap-1.5">
    {opts.map((m) => {
      const on = value.includes(m);
      return (
        <button key={m} type="button" data-testid={`${prefix}-${m.toLowerCase()}`} aria-pressed={on}
          onClick={() => onChange(on ? value.filter((x) => x !== m) : [...value, m])}
          className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${on ? "border-sky-400 bg-sky-500/20 text-sky-100" : "border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-white"}`}>
          {m}
        </button>
      );
    })}
  </div>
);

const SetPick = ({ tid, pick, onChange }) => {
  const pcs = pcsOf(pick.members);
  const miss = pick.hasMissing ? pcsOf(pick.missing) : 0;
  const setMembers = (v) => onChange({ ...pick, members: v, missing: pick.missing.filter((x) => v.includes(x) || (v.includes("OT8") && x !== "OT8")) });
  return (
    <div className="mt-2 space-y-2 rounded-lg border border-white/5 bg-zinc-950/60 p-3">
      <div className="text-xs text-zinc-400">Which member was claimed</div>
      <Chips value={pick.members} onChange={setMembers} prefix={`${tid}-member`} />
      <div data-testid={`${tid}-pcs`} className="text-xs text-zinc-400">{pcs} PC{pcs === 1 ? "" : "s"}</div>
      <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer">
        <Checkbox data-testid={`${tid}-missing-checkbox`} checked={pick.hasMissing} onCheckedChange={(v) => onChange({ ...pick, hasMissing: !!v })} /> Any Missing PCs?
      </label>
      {pick.hasMissing && (
        <>
          <Chips value={pick.missing} onChange={(v) => onChange({ ...pick, missing: v })} prefix={`${tid}-missing`} opts={pick.members.includes("OT8") ? OPTS : OPTS.filter((m) => pick.members.includes(m))} />
          <div data-testid={`${tid}-missing-pcs`} className="text-xs text-zinc-400">{miss} missing · EMS counts {Math.max(0, pcs - miss)} PCs</div>
        </>
      )}
    </div>
  );
};

const DropPick = ({ d, open, onToggle, picks, onPick }) => (
  <div data-testid={`higher-mc-drop-${d.id}`} className={`rounded-xl border p-3 transition-colors ${open ? "border-sky-400/50 bg-sky-500/5" : "border-zinc-800"}`}>
    <label className="flex items-center gap-2.5 text-sm font-semibold cursor-pointer">
      <Checkbox data-testid={`higher-mc-drop-check-${d.id}`} checked={open} onCheckedChange={onToggle} /> {d.name}
      <span className="ml-auto font-mono text-[11px] font-normal text-zinc-500">{d.total_pcs}/{d.total_available} PCs</span>
    </label>
    {open && (
      <div className="mt-3 space-y-2 pl-1">
        <div className="text-xs text-zinc-400">Tick one or more sets</div>
        {d.fill.map((f) => {
          const left = f.available - f.assigned;
          const key = `${d.id}|${f.name}`;
          const tid = `higher-mc-set-${d.id}-${slug(f.name)}`;
          return (
            <div key={f.name}>
              <label className={`flex items-center gap-2 text-sm ${left > 0 ? "cursor-pointer" : "opacity-50"}`}>
                <Checkbox data-testid={`${tid}-check`} disabled={left <= 0} checked={!!picks[key]} onCheckedChange={() => onPick(key, picks[key] ? null : { drop_id: d.id, set_name: f.name, members: [], missing: [], hasMissing: false })} />
                {f.name} <span className="text-xs text-zinc-500">· {left > 0 ? `${left} PCs left` : "Full"}</span>
              </label>
              {picks[key] && <SetPick tid={tid} pick={picks[key]} onChange={(v) => onPick(key, v)} />}
            </div>
          );
        })}
      </div>
    )}
  </div>
);

export default function HigherMemberClaimDialog({ joinerId, onClose, onSaved }) {
  const [drops, setDrops] = useState(null);
  const [openDrops, setOpenDrops] = useState([]);
  const [picks, setPicks] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/higher/drops").then((r) => setDrops(r.data.filter((d) => d.total_pcs < d.total_available))).catch(() => setDrops([]));
  }, []);
  const onPick = (key, v) => setPicks((p) => { const n = { ...p }; if (v) n[key] = v; else delete n[key]; return n; });
  const toggleDrop = (d) => {
    const isOpen = openDrops.includes(d.id);
    setOpenDrops((o) => (isOpen ? o.filter((x) => x !== d.id) : [...o, d.id]));
    if (isOpen) setPicks((p) => Object.fromEntries(Object.entries(p).filter(([k]) => !k.startsWith(`${d.id}|`))));
  };
  const items = Object.values(picks);
  const total = items.reduce((n, i) => n + pcsOf(i.members), 0);

  const save = async (e) => {
    e.preventDefault();
    if (!items.length) return toast.error("Tick at least one set in a Higher Drop");
    const empty = items.find((i) => !i.members.length);
    if (empty) return toast.error(`Pick which member(s) were claimed in ${empty.set_name}`);
    setBusy(true);
    try {
      await api.post(`/higher/joiners/${joinerId}/member-claims`, {
        items: items.map((i) => ({ drop_id: i.drop_id, set_name: i.set_name, members: i.members, missing: i.hasMissing ? i.missing : [] })),
      });
      toast.success(`Added ${total} PC${total === 1 ? "" : "s"} across ${items.length} set${items.length === 1 ? "" : "s"}`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errMsg(err, "Couldn't add claims"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="higher-member-claim-dialog" className="bg-zinc-950 border-zinc-800 sm:rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">+ Higher Drop</DialogTitle>
          <DialogDescription>Tick drops and sets, then pick members for each set. OT8 = 8 PCs, every other pick = 1 PC. Missing PCs don't count toward EMS.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-2">
            <Label>Higher Drops</Label>
            {drops === null && <div className="text-sm text-zinc-500">Loading…</div>}
            {drops?.length === 0 && <div data-testid="higher-mc-no-drops" className="text-sm text-zinc-500">No Higher Drops with room left.</div>}
            {drops?.map((d) => <DropPick key={d.id} d={d} open={openDrops.includes(d.id)} onToggle={() => toggleDrop(d)} picks={picks} onPick={onPick} />)}
          </div>
          <div data-testid="higher-mc-total" className="text-sm text-zinc-300">{items.length} set{items.length === 1 ? "" : "s"} · {total} PC{total === 1 ? "" : "s"}</div>
          <Button type="submit" disabled={busy} data-testid="higher-mc-save" className="w-full h-11 rounded-xl bg-white text-zinc-950 hover:bg-zinc-200 font-semibold">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Add claims
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
